import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import AppTable, { ColumnDef } from "../../../../../components/common/AppTable";
import AppSwitch from "../../../../../components/common/AppSwitch";
import { IMAGES } from "../../../../../constants/assetLists";
import QUERY_KEYS from "../../../../../constants/queryKeys";
import { useOrganizationsScreenContext } from "../../../../../context/organizations/OrganizationsScreenContextProvider";
import {
  AdminLocationType,
  handleUpdateAdminLocationStatusService,
  OrganizationState,
  OrganizationTableRow,
} from "../../../../../services/organizations";
import { useSnackbar } from "../../../../../context/SnackbarContextProvider";

const TYPE_LABELS: Record<string, string> = {
  restaurant: "מסעדה",
  cafe: "בית קפה",
  bar: "בר",
  bakery: "מאפיה",
  pizzeria: "פיצריה",
  group: "קבוצה",
  store: "חנות",
};

const STATUS_LABELS: Record<string, string> = {
  active: "פעיל",
  inactive: "לא פעיל",
  draft: "~טיוטה~",
};

const EMPTY_VALUE = "--";
const DRAFT_TEXT_CLASS = "text-[#707070]";

function getPrimaryContact(row: OrganizationTableRow) {
  return row.contacts?.[0];
}

function displayValue(value?: string | null) {
  return value?.trim() || EMPTY_VALUE;
}

export function getStatusLabel(status?: string | null) {
  return status ? (STATUS_LABELS[status] ?? status) : EMPTY_VALUE;
}

function getLocationType(row: OrganizationTableRow): AdminLocationType {
  if (row.organizationLevel === "network") return "client";
  if (row.organizationLevel === "branch") return "branch";
  return "subBranch";
}

function OrganizationsTable() {
  const queryClient = useQueryClient();
  const {
    organizationRows,
    selectedOrganizationTableRowIds,
    setSelectedOrganizationTableRowIds,
    searchText,
    setSelectedSideFilterOrganization,
    setSelectedTab,
    setIsAllOrganizationsSelected,
  } = useOrganizationsScreenContext();

  const { showSnackbar } = useSnackbar();

  const updateStatusMutation = useMutation({
    mutationFn: handleUpdateAdminLocationStatusService,
    onSuccess: (data ) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_ADMIN_ON_BOARDING_DRAFTS],
      });

      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_ON_BOARDING_DRAFT],
      });

      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_USER_CLIENT],
      });

      showSnackbar({
        message: data?.message || 'הסטטוס עודכן בהצלחה',
        type: 'success',
      });
    },
  });

  const sortedOrganizationRows = useMemo(
    () =>
      [...organizationRows].sort((a, b) => {
        if (!!a.isArchived === !!b.isArchived) return 0;
        return a.isArchived ? 1 : -1;
      }),
    [organizationRows],
  );

  const handleRowClick = (row: OrganizationTableRow) => {
    setSelectedOrganizationTableRowIds((current) =>
      current.includes(row.id)
        ? current.filter((id) => id !== row.id)
        : [...current, row.id],
    );
  };

  const handleRowDoubleClick = (row: OrganizationTableRow) => {
    setIsAllOrganizationsSelected(false);
    setSelectedSideFilterOrganization(row);
    setSelectedTab("organizationProfile");
  };

  const handleStatusChange = (row: OrganizationTableRow, checked: boolean) => {
    const nextStatus: Exclude<OrganizationState, "draft"> = checked
      ? "active"
      : "inactive";

    updateStatusMutation.mutate({
      locationType: getLocationType(row),
      locationId: row.id,
      status: nextStatus,
    });
  };

  const columns = useMemo<ColumnDef<OrganizationTableRow>[]>(
    () => [
      {
        title: "לוגו",
        field: "logo",
        accessor: (row) => row.logo?.url || IMAGES.placeholder,
        sortable: false,
        searchable: false,
        width: 80,
        render: (row) => (
          <div className="w-[50px] h-[50px] rounded-full overflow-hidden bg-gray95 flex items-center justify-center">
            <img
              src={row.logo?.url || IMAGES.placeholder}
              alt={displayValue(row.name)}
              className="w-full h-full object-contain"
            />
          </div>
        ),
      },
      {
        title: "בית עסק",
        field: "name",
        accessor: (row) => displayValue(row.name),
        sortable: true,
        searchable: true,
      },
      {
        title: "סוג",
        field: "type",
        accessor: (row) =>
          row.type ? (TYPE_LABELS[row.type] ?? row.type) : EMPTY_VALUE,
        sortable: true,
      },
      {
        title: "איפיון",
        field: "characterization",
        accessor: (row) => {
          if (row.organizationLevel === "network") return "סניף ראשי";
          if (row.organizationLevel === "branch") return "סניף";
          return "תת סניף";
        },
        sortable: true,
      },
      {
        title: "סטאטוס",
        field: "status",
        accessor: (row) => getStatusLabel(row.status),
        sortable: true,
        render: (row) => {
          if (row.status === "draft") {
            return <span className="italic">{STATUS_LABELS.draft}</span>;
          }

          const isActive = row.status === "active";

          return (
            <div
              className="flex items-center gap-2"
              onClick={(event) => event.stopPropagation()}
              onDoubleClick={(event) => event.stopPropagation()}
            >
              <AppSwitch
                checked={isActive}
                outlined={!isActive}
                disabled={updateStatusMutation.isPending || !!row.isArchived}
                onChange={(event) =>
                  handleStatusChange(row, event.target.checked)
                }
              />
              <span>{getStatusLabel(row.status)}</span>
            </div>
          );
        },
      },
      {
        title: "איש קשר",
        field: "contact",
        accessor: (row) => {
          const contact = getPrimaryContact(row);
          return (
            [contact?.firstName, contact?.lastName].filter(Boolean).join(" ") ||
            EMPTY_VALUE
          );
        },
        sortable: true,
      },
      {
        title: "תפקיד בעסק",
        field: "businessRole",
        accessor: (row) => displayValue(getPrimaryContact(row)?.role),
        sortable: true,
      },
      {
        title: "תפקיד במערכת",
        field: "systemRole",
        accessor: (row) => displayValue(getPrimaryContact(row)?.systemRole),
        sortable: true,
      },
      {
        title: "טלפון",
        field: "phone",
        accessor: (row) =>
          displayValue(getPrimaryContact(row)?.phone ?? row.phone),
        sortable: true,
        render: (row) => {
          const phone = displayValue(
            getPrimaryContact(row)?.phone ?? row.phone,
          );
          return <span dir="ltr">{phone}</span>;
        },
      },
      {
        title: "כתובת",
        field: "address",
        accessor: (row) => displayValue(row.address),
        sortable: true,
      },
    ],
    [updateStatusMutation.isPending],
  );

  return (
    <AppTable<OrganizationTableRow>
      data={sortedOrganizationRows}
      columns={columns}
      rowKey={(row) => row.id}
      selectable
      selectedKeys={selectedOrganizationTableRowIds}
      onSelectionChange={setSelectedOrganizationTableRowIds}
      searchText={searchText}
      cellTextClass={(row) =>
        row.status === "draft" ? DRAFT_TEXT_CLASS : undefined
      }
      rowDisabled={(row) => !!row.isArchived}
      onRowClick={handleRowClick}
      onRowDoubleClick={handleRowDoubleClick}
      bodyCellBaseClass="border-1 border-gray70 p-[10px] align-middle"
      checkboxCellClassName="border-1 border-gray70 px-[12px] py-[8px] align-middle w-[50px]"
    />
  );
}

export default OrganizationsTable;
